import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import { Badge } from '@/components/ui/badge';
import type { GuiaVersao, TagVersao } from '@/data/types';
import { SEM_ATUALIZACOES_ID } from '@/data/sources/rest/normalize';
import { cn } from '@/lib/utils';

const BADGE_POR_TAG: Record<TagVersao, { tom: 'primary' | 'success' | 'warning' | 'info'; rotulo: string }> = {
  recomendada: { tom: 'primary', rotulo: 'Recomendada' },
  homologada: { tom: 'success', rotulo: 'Homologada' },
  nao_homologada: { tom: 'warning', rotulo: 'Não homologada' },
  retentativa: { tom: 'info', rotulo: 'Retentativa' },
};

/**
 * VersaoGuiasSelect — combo de versão com guias internas (LD/QA/Master).
 *
 * Irmão do `SearchSelect`, sem campo de busca (a lista de cada guia é curta):
 * o trigger abre um popover com uma guia por estágio que o backend mandou —
 * guias abaixo do estágio atual da instalação já não vêm na lista (ver
 * `GuiaVersao`), então não há nada a esconder aqui, só a navegar entre elas.
 */
export function VersaoGuiasSelect({
  id,
  valor,
  aoMudar,
  guias,
  placeholder,
  describedBy,
  disabled,
}: {
  id: string;
  valor: string | null;
  aoMudar: (valor: string) => void;
  guias: GuiaVersao[];
  placeholder: string;
  describedBy?: string;
  disabled?: boolean;
}) {
  const [aberto, setAberto] = React.useState(false);

  const guiaDaSelecao = guias.find((guia) => guia.versoes.some((item) => item.id === valor));
  const [abaAtiva, setAbaAtiva] = React.useState<string | null>(guiaDaSelecao?.chave ?? guias[0]?.chave ?? null);

  // Guias mudam quando a seleção de instalações muda (etapa 1) — se a aba
  // ativa não existir mais no novo conjunto, cai pra primeira disponível.
  React.useEffect(() => {
    if (guias.some((guia) => guia.chave === abaAtiva)) return;
    setAbaAtiva(guiaDaSelecao?.chave ?? guias[0]?.chave ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guias]);

  const guiaAtual = guias.find((guia) => guia.chave === abaAtiva) ?? guias[0] ?? null;
  const selecionada = guias.flatMap((guia) => guia.versoes).find((item) => item.id === valor) ?? null;

  function escolher(itemId: string, desabilitada: boolean) {
    if (desabilitada) return;
    aoMudar(itemId);
    setAberto(false);
  }

  return (
    <PopoverPrimitive.Root open={aberto} onOpenChange={setAberto}>
      <PopoverPrimitive.Trigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={aberto}
          aria-describedby={describedBy}
          disabled={disabled}
          className="flex h-10 w-full items-center justify-between gap-2 rounded-input border border-border-strong bg-bg-surface px-3 text-body text-on-surface transition-colors duration-fast ease-standard hover:border-primary disabled:cursor-not-allowed disabled:bg-bg-subtle disabled:text-on-surface-muted"
        >
          <span className={cn('truncate', !selecionada && 'text-on-surface-muted')}>
            {selecionada ? selecionada.nome : placeholder}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-icon" aria-hidden="true" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] min-w-64 overflow-hidden rounded-input border border-border bg-bg-surface shadow-md duration-base ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          {guias.length > 1 ? (
            <div role="tablist" aria-label="Estágio da versão" className="flex border-b border-border">
              {guias.map((guia) => {
                const ativa = guia.chave === abaAtiva;
                return (
                  <button
                    key={guia.chave}
                    type="button"
                    role="tab"
                    aria-selected={ativa}
                    onClick={() => setAbaAtiva(guia.chave)}
                    className={cn(
                      'flex flex-1 flex-col items-center gap-0.5 border-b-2 px-3 py-2.5 transition-colors duration-fast ease-standard',
                      ativa ? 'border-primary' : 'border-transparent hover:bg-bg-subtle',
                    )}
                  >
                    <span className={cn('text-label font-bold', ativa ? 'text-primary' : 'text-on-surface')}>
                      {guia.rotulo}
                    </span>
                    <span className={cn('text-micro font-normal', ativa ? 'text-primary' : 'text-on-surface-muted')}>
                      {guia.ramo}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}

          <ul role="listbox" aria-label={placeholder} className="pan-scroll-area max-h-64 overflow-y-auto p-1">
            {(guiaAtual?.versoes ?? []).map((item) => {
              const ativa = item.id === valor;
              const desabilitada = item.id === SEM_ATUALIZACOES_ID;
              const badge = item.tag ? BADGE_POR_TAG[item.tag] : null;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={ativa}
                    disabled={desabilitada}
                    onClick={() => escolher(item.id, desabilitada)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-chip px-2 py-2 text-left text-body transition-colors duration-fast ease-standard',
                      desabilitada ? 'cursor-not-allowed text-on-surface-muted' : 'hover:bg-bg-subtle',
                    )}
                  >
                    <Check className={cn('size-4 shrink-0 text-primary', !ativa && 'invisible')} aria-hidden="true" />
                    {badge ? (
                      <>
                        <span className="flex-1 truncate font-narrow font-bold tabular-nums">{item.nome}</span>
                        <Badge tone={badge.tom}>{badge.rotulo}</Badge>
                      </>
                    ) : (
                      <span className="flex-1 truncate">{item.nome}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
